import React, { useEffect, useRef } from "react";
import { Eye, Grid2x2, Tag, Layers, Clock, Sparkles } from "lucide-react";
import { compileMall } from "../../lib/mallRegistry";
import { drawAvatar } from "../../lib/avatarDraw";
import { lukisPerabotMall, AVATAR_CONTOH } from "./MallPreview";
import { pill, GLASS, FONT, SAFE_TOP, glassInner } from "../kota/ui";
import { chipStyle } from "../kota/Sheet";

const ATAS = 162;      // ruang bar atas + tab + chip (px, di luar safe area)
const BLOK_NAMA = 96;  // tinggi blok nama + tombol di atas panel

// Lukis panggung: latar krem kotak-kotak lembut + alas bundar putih (cincin putus-putus) + barang terpilih di atasnya.
function drawStage(ctx, W, H, item, fn, avatar, t, panelH) {
  ctx.fillStyle = "#f3efe3"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#eae5d6";
  const s = 30;
  for (let y = 0; y < H; y += s) for (let x = (Math.floor(y / s) % 2) * s; x < W; x += 2 * s) ctx.fillRect(x, y, s, s);

  // alas bundar: tepi bawahnya (termasuk sisi tebal) berjarak 8 px dari blok nama; barang berdiri di pusat alas
  const bawah = H - panelH - BLOK_NAMA, ruang = Math.max(110, bawah - ATAS), tebal = 12;
  const rx = Math.min(W * 0.34, 150, ruang * 0.7), ry = rx * 0.42;
  const cx = W / 2, cy = bawah - ry - tebal - 8;
  const ell = (x, y, a, b) => { ctx.beginPath(); ctx.ellipse(x, y, a, b, 0, 0, Math.PI * 2); };
  ctx.fillStyle = "rgba(20,20,20,.08)"; ell(cx, cy + tebal + 7, rx * 1.05, ry * 1.08); ctx.fill();
  ctx.fillStyle = "#d9d3c2"; ctx.beginPath(); ctx.ellipse(cx, cy + tebal, rx, ry, 0, 0, Math.PI); ctx.lineTo(cx - rx, cy); ctx.ellipse(cx, cy, rx, ry, 0, Math.PI, 0, true); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#fbf9f3"; ell(cx, cy, rx, ry); ctx.fill();
  ctx.setLineDash([5, 6]); ctx.lineWidth = 1.2; ctx.strokeStyle = "rgba(0,0,205,.28)"; ell(cx, cy, rx * 0.84, ry * 0.84); ctx.stroke(); ctx.setLineDash([]);
  if (!item) return;

  const tinggiBarang = Math.max(80, cy - ATAS - 8);   // ruang vertikal untuk barang di atas pusat alas
  if (item.jenis === "aksesoris") {
    const sc = Math.max(1.4, Math.min(3.2, (tinggiBarang + ry * 0.3) / 62));
    drawAvatar(ctx, cx, cy + ry * 0.3, avatar || AVATAR_CONTOH, { scale: sc, t, mallCoba: fn ? [{ fn, kategori: item.kategori }] : [] });
  } else {
    const w = item.w || 1, d = item.d || 1;
    const T = Math.max(28, Math.min(120, (rx * 3) / (w + d), tinggiBarang / ((w + d) / 8 + 1.05)));
    lukisPerabotMall(ctx, fn, w, d, cx, cy, T, t);
  }
  if (!fn) { ctx.fillStyle = "rgba(20,20,20,.45)"; ctx.font = `700 12px ${FONT}`; ctx.textAlign = "center"; ctx.fillText(item.paintCode ? "kode barang bermasalah" : "belum ada gambar", cx, cy - ry - 24); }
}

// Panggung pratinjau barang terpilih (kanvas latar penuh) + chip info + blok nama & aksi "Coba di rumahku" / "Di avatarku".
export function MallStage({ item, avatar, cobaAvatar, panelH, punya, dipakai, onCoba, onToggleAvatar }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return undefined;
    const ctx = c.getContext("2d"), fn = item ? compileMall(item.paintCode, item.jenis) : null, t0 = performance.now();
    let raf, W = 0, H = 0, dpr = 1;
    const ukur = () => { dpr = Math.min(2, window.devicePixelRatio || 1); W = c.clientWidth; H = c.clientHeight; c.width = Math.round(W * dpr); c.height = Math.round(H * dpr); };
    ukur();
    const ro = new ResizeObserver(ukur); ro.observe(c);
    const gambar = () => { ctx.setTransform(dpr, 0, 0, dpr, 0, 0); drawStage(ctx, W, H, item, fn, cobaAvatar ? avatar : null, (performance.now() - t0) / 1000, panelH); raf = requestAnimationFrame(gambar); };
    gambar();
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [item, avatar, cobaAvatar, panelH]);

  const aks = item?.jenis === "aksesoris", segera = item?.pasang === "segera";
  const chip = (Icon, teks, tid) => <span key={teks} data-testid={tid} style={{ ...pill, height: 28, padding: "0 10px", gap: 5, fontSize: 11, fontWeight: 700 }}><Icon size={11} color={GLASS.sub} /> {teks}</span>;
  return (
    <>
      <canvas ref={ref} data-testid="mall-stage" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }} aria-hidden />
      {item && (
        <div data-testid="mall-stage-chips" style={{ position: "absolute", left: 14, right: 14, top: `calc(${SAFE_TOP} + 122px)`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, pointerEvents: "none", fontFamily: FONT, zIndex: 5 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
            {chip(Tag, item.kategori_nama, "mall-chip-kategori")}
            {!aks && chip(Grid2x2, `${item.w || 1}×${item.d || 1} petak`, "mall-chip-petak")}
            {item.set && chip(Layers, `Set ${item.set}`, "mall-chip-set")}
          </div>
          {punya > 0 && <span data-testid="mall-stage-punya" style={{ ...chipStyle("green"), flex: "none" }}>{aks ? (dipakai ? "Dipakai" : "Punya") : `Punya ${punya}`}</span>}
        </div>
      )}
      <div data-testid="mall-stage-nama" style={{ position: "absolute", left: 16, right: 16, bottom: panelH + 12, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10, pointerEvents: "none", fontFamily: FONT, zIndex: 5 }}>
        <div style={{ minWidth: 0 }}>
          <h2 data-testid="mall-nama" style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: -0.4, color: GLASS.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item ? item.nama : "Mall Jawsel"}</h2>
          <p data-testid="mall-sub" style={{ margin: "3px 0 0", fontSize: 12.5, color: GLASS.sub, fontWeight: 600, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {item ? (item.deskripsi || (aks ? "Aksesoris avatar · dipakai di Alun-alun, rumah & halaman warga" : segera ? "Barang halaman & eksterior" : "Perabot dalam rumah · taruh, geser, putar sesukamu")) : "Barang rumah & aksesoris avatar hasil Studio Jawsel"}
          </p>
        </div>
        {item && !aks && !segera && (
          <button data-testid="mall-coba" onClick={onCoba} style={{ ...pill, pointerEvents: "auto", cursor: "pointer", height: 40, padding: "0 14px", gap: 7, flex: "none", background: GLASS.accent, color: "#fff", border: 0, fontWeight: 800 }}><Eye size={15} /> Coba di rumahku</button>
        )}
        {item && aks && avatar && (
          <button data-testid="mall-coba-avatar" aria-pressed={cobaAvatar} onClick={onToggleAvatar} style={{ ...pill, pointerEvents: "auto", cursor: "pointer", height: 36, padding: "0 12px", gap: 6, flex: "none", ...glassInner(cobaAvatar), color: cobaAvatar ? GLASS.accent : GLASS.ink, fontWeight: 700 }}><Eye size={14} /> {cobaAvatar ? "Di avatarku" : "Avatar contoh"}</button>
        )}
        {item && segera && <span data-testid="mall-segera" style={{ ...pill, height: 32, padding: "0 12px", gap: 6, fontSize: 11.5, flex: "none", color: GLASS.sub }}><Clock size={12} /> Pemasangan segera hadir</span>}
        {!item && <span style={{ ...pill, height: 32, padding: "0 12px", gap: 6, fontSize: 11.5, flex: "none", color: GLASS.sub }}><Sparkles size={12} /> Pilih barang di rak</span>}
      </div>
    </>
  );
}
